import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Utility function to construct full image URLs
export function getImageUrl(imagePath: string | null | undefined): string {
  if (!imagePath) return "/banners/no-img.jpg"
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath
  }
  
  // If it's a relative path, construct full URL
  if (imagePath.startsWith('/')) {
    return `${process.env.NEXT_PUBLIC_API_URL}${imagePath}`
  }
  
  // If it's just a filename, construct full URL
  return `${process.env.NEXT_PUBLIC_API_URL}/storage/products/${imagePath}`
}

export async function fetchProducts(page = 1) {
  const apiUrl = getApiUrl()
  const url = `${apiUrl}/products?page=${page}`
  console.log('Fetching products from:', url)
  console.log('API URL base:', apiUrl)
  console.log('Page parameter:', page)
  
  try {
    const res = await fetch(url, {
      headers: {
        'Accept': 'application/json',
      }
    });
    
    console.log('Products response status:', res.status)
    console.log('Products response headers:', res.headers)
    
    if (!res.ok) {
      console.error('Products fetch failed with status:', res.status)
      const errorText = await res.text()
      console.error('Error response body:', errorText)
      throw new Error(`Failed to fetch products: ${res.status} ${res.statusText}`);
    }
    
    const data = await res.json()
    console.log('Products response data:', data)
    console.log('Products response data type:', typeof data)
    console.log('Products response data keys:', Object.keys(data))
    return data;
  } catch (error) {
    console.error('Error in fetchProducts:', error)
    throw error;
  }
}

export async function fetchFeaturedProducts() {
  const res = await fetch(`${getApiUrl()}/featured`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch featured products');
  return res.json();
}

export async function fetchNewProducts() {
  const res = await fetch(`${getApiUrl()}/new`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch new products');
  return res.json();
}

export async function searchProducts(query: string) {
  const res = await fetch(`${getApiUrl()}/search/${encodeURIComponent(query)}`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to search products');
  return res.json();
}

export async function fetchProductById(id: number | string) {
  const res = await fetch(`${getApiUrl()}/products/${id}`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch product');
  return res.json();
}

export async function fetchCategories(page = 1, per_page = 100) {
  const res = await fetch(`${getApiUrl()}/categories?page=${page}&per_page=${per_page}`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch categories');
  return res.json();
}

export async function fetchAllCategories() {
  const res = await fetch(`${getApiUrl()}/categories?per_page=100`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch all categories');
  const data = await res.json();
  return data;
}

export async function fetchBrands(page = 1) {
  const res = await fetch(`${getApiUrl()}/brands?page=${page}`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch brands');
  return res.json();
}

export async function fetchAllBrands() {
  const res = await fetch(`${getApiUrl()}/brands?per_page=100`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch all brands');
  return res.json();
}

export async function fetchSubcategories(categoryId?: number) {
  const url = categoryId 
    ? `${getApiUrl()}/subcategories/${categoryId}`
    : `${getApiUrl()}/subcategories`;
  const res = await fetch(url, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch subcategories');
  return res.json();
}

export async function fetchAllSubcategories() {
  const res = await fetch(`${getApiUrl()}/subcategories?per_page=100`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch all subcategories');
  return res.json();
}

export async function fetchConditions() {
  const res = await fetch(`${getApiUrl()}/conditions`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch conditions');
  return res.json();
}

export async function fetchAllConditions() {
  const res = await fetch(`${getApiUrl()}/conditions?per_page=100`, {
    headers: {
      'Accept': 'application/json',
    }
  });
  if (!res.ok) throw new Error('Failed to fetch all conditions');
  return res.json();
}

export async function fetchFilteredProducts(filters: {
  category_id?: string | number;
  brand_id?: string | number;
  condition_id?: string | number;
  subcategory_id?: string | number;
  min_price?: number;
  max_price?: number;
  in_stock?: boolean;
  search?: string;
  sort_by?: string;
  page?: number;
  per_page?: number;
}) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value.toString());
    }
  });

  const apiUrl = getApiUrl()
  const url = `${apiUrl}/filter-products?${params.toString()}`
  console.log('Fetching filtered products from:', url)
  console.log('Filters:', filters)

  const res = await fetch(url, {
    headers: {
      'Accept': 'application/json',
    }
  });
  
  console.log('Filtered products response status:', res.status)
  
  if (!res.ok) {
    console.error('Filtered products fetch failed with status:', res.status)
    throw new Error('Failed to fetch filtered products');
  }
  
  const data = await res.json()
  console.log('Filtered products response data:', data)
  return data;
}

export async function addToCart(productId: number, quantity: number = 1) {
  const res = await fetch(`${getApiUrl()}/addToCart`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ product_id: productId, quantity }),
  });
  console.log('addToCart', res);
  if (!res.ok) {
    const errorData = await res.json();
    console.log('addToCart error', errorData);
    throw new Error(errorData.message || 'Failed to add item to cart');
  }
  return res.json();
}

export async function updateCartItem(productId: number, quantity: number) {
  const res = await fetch(`${getApiUrl()}/updateCart`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ product_id: productId, quantity }),
  });
  if (!res.ok) throw new Error('Failed to update cart item');
  return res.json();
}

export async function removeFromCart(productId: number) {
  const res = await fetch(`${getApiUrl()}/cart-remove/${productId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to remove item from cart');
  return res.json();
}

export async function clearCart() {
  const res = await fetch(`${getApiUrl()}/cart-clear`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to clear cart');
  return res.json();
}

export async function getCart() {
  const res = await fetch(`${getApiUrl()}/cart`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch cart');
  return res.json();
}

export async function getCartTotal() {
  const res = await fetch(`${getApiUrl()}/cart-total`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch cart total');
  return res.json();
}

export async function getCartCount() {
  const res = await fetch(`${getApiUrl()}/cart-count`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch cart count');
  return res.json();
}

export async function createOrder(orderData: any) {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/create-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });
  if (!res.ok) throw new Error('Failed to create order');
  return res.json();
}

export async function placeOrder(orderData: any) {
  const res = await fetch(`${getApiUrl()}/place-order`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData),
  });
  if (!res.ok) throw new Error('Failed to place order');
  return res.json();
}

export async function initiatePayment(orderId: number) {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/initiatePayment/${orderId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to initiate payment');
  return res.json();
}

export async function mpesaStkPush(phoneNumber: string, amount: number, orderId: number) {
  const res = await fetch(`${getApiUrl()}/mpesa/stkpush`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      phone_number: phoneNumber,
      amount: amount,
      order_id: orderId,
    }),
  });
  if (!res.ok) throw new Error('Failed to initiate M-Pesa payment');
  return res.json();
}

export async function confirmPayment(orderId: number, paymentData: any) {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/mpesa/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      order_id: orderId,
      ...paymentData,
    }),
  });
  if (!res.ok) throw new Error('Failed to confirm payment');
  return res.json();
}

export async function checkPaymentStatus(orderId: number) {
  const res = await fetch(`${getApiUrl()}/checkPayment/${orderId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to check payment status');
  return res.json();
}

// Helper function to get API URL with fallback
const getApiUrl = () => {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api'
  console.log('getApiUrl called, returning:', apiUrl)
  return apiUrl
}

// Helper function to create fetch with timeout
const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = 10000) => {
  console.log('Fetching with timeout:', options)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    throw error;
  }
}

  export async function login(credentials: { username: string; password: string }) {
  console.log('Attempting login with:', credentials.username)
  const apiUrl = getApiUrl()
  console.log('API URL:', `${apiUrl}/login`)
  
  const res = await fetchWithTimeout(`${apiUrl}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });
  
  console.log('Login response status:', res.status)
  
  if (!res.ok) {
    const errorData = await res.json();
    console.log('Login error data:', errorData)
    throw new Error(errorData.message || 'Login failed');
  }
  
  const responseData = await res.json();
  console.log('Login success data:', responseData)
  return responseData;
}

export async function register(userData: { 
  name: string; 
  email: string; 
  password: string; 
  password_confirmation: string;
  phone?: string;
}) {
  console.log('Attempting registration with:', userData.email)
  const apiUrl = getApiUrl()
  console.log('API URL:', `${apiUrl}/register`)
  
  const res = await fetchWithTimeout(`${apiUrl}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
  
  console.log('Register response status:', res.status)
  
  if (!res.ok) {
    const errorData = await res.json();
    console.log('Register error data:', errorData)
    throw new Error(errorData.message || 'Registration failed');
  }
  
  const responseData = await res.json();
  console.log('Register success data:', responseData)
  return responseData;
}

export async function logout() {
  const res = await fetch(`${getApiUrl()}/logout`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Logout failed');
  return res.json();
}

export async function getUser() {
  const res = await fetch(`${getApiUrl()}/user`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to get user data');
  return res.json();
}

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = getAuthToken()
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}

// Helper function to get auth token
export const getAuthToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('auth_token')
  }
  return null
}

// Helper function to set auth token
export const setAuthToken = (token: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth_token', token)
  }
}

// Helper function to remove auth token
export const removeAuthToken = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth_token')
  }
}

// Helper function to check if user is authenticated
export const isAuthenticated = () => {
  return !!getAuthToken()
}

// Payment method utilities
export const getPaymentMethodDisplayName = (method: string) => {
  switch (method) {
    case 'mpesa':
      return 'M-Pesa'
    case 'cod':
      return 'Cash on Delivery'
    case 'insurance':
      return 'Insurance'
    default:
      return method
  }
}

export const getPaymentMethodIcon = (method: string) => {
  switch (method) {
    case 'mpesa':
      return 'phone'
    case 'cod':
      return 'truck'
    case 'insurance':
      return 'credit-card'
    default:
      return 'credit-card'
  }
}

export const validateInsuranceDetails = (provider: string, number: string, policy: string) => {
  const errors: string[] = []
  
  if (!provider) {
    errors.push('Insurance provider is required')
  }
  
  if (!number) {
    errors.push('Insurance number is required')
  }
  
  if (!policy) {
    errors.push('Policy number is required')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

export const formatPhoneForMpesa = (phone: string) => {
  if (!phone) return ""
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '')
  
  // If it starts with 0, replace with 254
  if (cleaned.startsWith('0')) {
    return '254' + cleaned.substring(1)
  }
  
  // If it doesn't start with 254, add it
  if (!cleaned.startsWith('254')) {
    return '254' + cleaned
  }
  
  return cleaned
}

export async function sendLoginOtp(phone: string) {
  const res = await fetch(`${getApiUrl()}/send-login-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Failed to send OTP');
  }
  
  return res.json();
}

export async function verifyLoginOtp(phone: string, otp: string) {
  const res = await fetch(`${getApiUrl()}/verify-login-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone, otp }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Invalid OTP');
  }
  
  return res.json();
}

export async function verifyEmail(email: string, otp: string) {
  const res = await fetch(`${getApiUrl()}/verify-email`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ email, otp }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Email verification failed');
  }
  
  return res.json();
}

export async function resendEmailVerification(email: string) {
  const res = await fetch(`${getApiUrl()}/resend-email-verification`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ email }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Failed to resend verification');
  }
  
  return res.json();
}

export async function sendPasswordResetOtp(email: string) {
  const res = await fetch(`${getApiUrl()}/otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Failed to send password reset OTP');
  }
  
  return res.json();
}

export async function verifyPasswordResetOtp(email: string, otp: string) {
  const res = await fetch(`${getApiUrl()}/password/verify-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, otp }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Invalid OTP');
  }
  
  const data = await res.json();
  return data; // This returns { status: 200, message: 'Verified', token: '...' }
}

export async function resetPassword(email: string, token: string, password: string, password_confirmation: string) {
  const res = await fetch(`${getApiUrl()}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      email, 
      token, 
      password, 
      password_confirmation 
    }),
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Failed to reset password');
  }
  
  return res.json();
}

// Discount utilities
export const getDiscountConfig = () => {
  const applyDiscount = process.env.NEXT_PUBLIC_APPLY_DISCOUNT === 'true'
  const discountPercentage = parseFloat(process.env.NEXT_PUBLIC_DISCOUNT || '0')
  
  return {
    applyDiscount,
    discountPercentage
  }
}

export const calculateDiscountedPrice = (originalPrice: number): { discountedPrice: number; discountAmount: number } => {
  const { applyDiscount, discountPercentage } = getDiscountConfig()
  
  if (!applyDiscount || discountPercentage <= 0) {
    return {
      discountedPrice: originalPrice,
      discountAmount: 0
    }
  }
  
  const discountAmount = (originalPrice * discountPercentage) / 100
  const discountedPrice = originalPrice - discountAmount
  
  return {
    discountedPrice: Math.round(discountedPrice * 100) / 100, // Round to 2 decimal places
    discountAmount: Math.round(discountAmount * 100) / 100
  }
}

export const getDiscountPercentage = (): number => {
  const { discountPercentage } = getDiscountConfig()
  return discountPercentage
}

export const isDiscountActive = (): boolean => {
  const { applyDiscount } = getDiscountConfig()
  return applyDiscount
}
